"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DeliveryRulesPrettyPrinter = exports.DeliveryRulesEditor = exports.DeliveryRulesCalculator = exports.DeliveryRoute = exports.DeliveryRulesGeneralRegion = exports.getWeekdayName = exports.getWeekdayNumber = exports.getWeekdayKey = void 0;
const lodash_1 = __importDefault(require("lodash"));
// Moments locale needs to be english for the definitions of week days to be correct
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const customerSubUnit_1 = require("../customerSubUnit");
moment_timezone_1.default.locale('us');
const weekdaysMapping = {
    mon: 1,
    '1': 'mon',
    tue: 2,
    '2': 'tue',
    wed: 3,
    '3': 'wed',
    thu: 4,
    '4': 'thu',
    fri: 5,
    '5': 'fri',
    sat: 6,
    '6': 'sat',
    sun: 0,
    '0': 'sun',
};
const weekdays = [
    { key: 'mon', value: 'Måndag' },
    { key: 'tue', value: 'Tisdag' },
    { key: 'wed', value: 'Onsdag' },
    { key: 'thu', value: 'Torsdag' },
    { key: 'fri', value: 'Fredag' },
    { key: 'sat', value: 'Lördag' },
    { key: 'sun', value: 'Söndag' },
];
const weekdayMap = lodash_1.default.keyBy(weekdays, 'key');
const getWeekdayKey = (weekday) => {
    if (!isNaN(Number(weekday))) {
        return weekdaysMapping[weekday];
    }
    return weekday;
};
exports.getWeekdayKey = getWeekdayKey;
const getWeekdayNumber = (weekday) => {
    if (!isNaN(Number(weekday))) {
        return weekday;
    }
    return weekdaysMapping[weekday];
};
exports.getWeekdayNumber = getWeekdayNumber;
const getWeekdayName = (weekday) => {
    const key = (0, exports.getWeekdayKey)(weekday);
    return weekdayMap[key]?.value;
};
exports.getWeekdayName = getWeekdayName;
/*
Controlled by customer:
on supplierConnection or customerSubUnit:
{
  selectedRoute: {weekday: 'mon'}
}

Controlled by supplier:
on supplierCustomer:
{
  delivery: {weekday: 'mon' OR date: '2021-01-01'},
  orderBy: {daysBefore: 1, time: '12:00'},
}

on orderFullfillmentCenter.deliveryRules:
{
  delivery: {weekday: 'mon'},
  orderBy: {daysBefore: 1, time: '12:00'},
  applicableWhen: {validZipCodes: ['12345', '54321']}
}

on orderFullfillmentCenter.deliveryRuleOverrides:
{
  delivery: {date: '2021-01-01'},
  orderBy: {daysBefore: 1, time: '12:00'},
  applicableWhen: {validZipCodes: ['12345', '54321']}
}
*/
exports.DeliveryRulesGeneralRegion = 'general';
function matchBasedOnZipCodeRange(zipCode, rule) {
    if (!rule.applicableWhen?.validZipCodes) {
        return false;
    }
    return rule.applicableWhen.validZipCodes.some(zipCodePattern => {
        const splitOnDash = zipCodePattern.split('-');
        if (splitOnDash && splitOnDash.length === 2) {
            const start = parseInt(splitOnDash[0].replaceAll('x', '0'), 10);
            const end = parseInt(splitOnDash[1].replaceAll('x', '9'), 10);
            return start <= parseInt(zipCode, 10) && end >= parseInt(zipCode, 10);
        }
        return false;
    });
}
function matchBasedOnZipCodePattern(zipCode, rule) {
    if (!rule.applicableWhen?.validZipCodes) {
        return false;
    }
    return rule.applicableWhen.validZipCodes.some(zipCodePattern => {
        const patternString = zipCodePattern.replaceAll('x', '.');
        const zipCodeRegex = new RegExp(patternString);
        return zipCodeRegex.test(zipCode);
    });
}
// ======================================
// DeliveryRoute
// ======================================
class DeliveryRoute {
    constructor(definition) {
        this.definition = definition ?? {};
    }
    static deliveryRouteFromProps(options) {
        const definition = {
            delivery: {},
            orderBy: {
                daysBefore: options.orderDaysBefore,
                time: options.orderByTime,
            },
        };
        if (options.date !== undefined) {
            definition.delivery.date = options.date;
        }
        else if (options.deliveryWeekDay !== undefined) {
            definition.delivery.weekday = options.deliveryWeekDay;
        }
        if (options.blocked) {
            definition.delivery.blocked = options.blocked;
        }
        if (!lodash_1.default.isNil(options.region)) {
            definition.applicableWhen = {
                validZipCodes: [options.region],
            };
        }
        return new DeliveryRoute(definition);
    }
    static createSelectedRoute(weekday) {
        return new DeliveryRoute({
            selectedRoute: { weekday },
        });
    }
    getDefinition() {
        return this.definition;
    }
    isValid() {
        return ((this.definition.delivery && !this.definition.delivery.blocked) ||
            this.definition.selectedRoute);
    }
    getKey() {
        return (this.definition.delivery?.weekday ??
            this.definition.delivery?.date ??
            this.definition.selectedRoute?.weekday ??
            null);
    }
    getDeliveryWeekday() {
        return this.definition.delivery?.weekday;
    }
    setDeliveryWeekday(weekday) {
        lodash_1.default.set(this.definition, 'delivery.weekday', weekday);
    }
    getSelectedWeekday() {
        return this.definition.selectedRoute?.weekday;
    }
    setSelectedWeekday(weekday) {
        lodash_1.default.set(this.definition, 'selectedRoute.weekday', weekday);
    }
    getDate() {
        return this.definition.delivery?.date;
    }
    setDate(date) {
        lodash_1.default.set(this.definition, 'delivery.date', date);
    }
    getRegion() {
        return (this.definition.applicableWhen?.validZipCodes?.join(',') ||
            exports.DeliveryRulesGeneralRegion);
    }
    setRegion(region) {
        if (region === exports.DeliveryRulesGeneralRegion) {
            lodash_1.default.unset(this.definition, 'applicableWhen.validZipCodes');
            return;
        }
        lodash_1.default.set(this.definition, 'applicableWhen.validZipCodes', region.split(','));
    }
    getTime() {
        return this.definition.orderBy?.time;
    }
    setTime(time) {
        lodash_1.default.set(this.definition, 'orderBy.time', time);
    }
    getDaysBefore() {
        return this.definition.orderBy?.daysBefore;
    }
    setDaysBefore(daysBefore) {
        lodash_1.default.set(this.definition, 'orderBy.daysBefore', daysBefore);
    }
    getBlocked() {
        return this.definition.delivery?.blocked;
    }
    setBlocked(blocked) {
        lodash_1.default.set(this.definition, 'delivery.blocked', blocked);
    }
    getBreakTimeForDate(forDate, forZipCode) {
        if (!this.definition ||
            !this.definition.delivery ||
            this.definition.delivery?.blocked) {
            return null;
        }
        if (this.definition.orderBy) {
            moment_timezone_1.default.tz.setDefault('Europe/Stockholm');
            const dayToCheck = (0, moment_timezone_1.default)(forDate).subtract(this.definition.orderBy.daysBefore, 'days');
            const timeParts = this.definition.orderBy.time.split(':');
            const breakTimeForDateInUtc = dayToCheck
                .hour(timeParts[0] ? parseInt(timeParts[0], 10) : 0)
                .minute(timeParts[1] ? parseInt(timeParts[1], 10) : 0)
                .toDate();
            moment_timezone_1.default.tz.setDefault();
            return breakTimeForDateInUtc;
        }
        return forDate;
    }
}
exports.DeliveryRoute = DeliveryRoute;
// ======================================
// DeliveryRules
// ======================================
class DeliveryRules {
    initializeRules(deliveryRules) {
        this.deliveryRoutesByWeekDay = {};
        this.deliveryRoutesByRegion = {};
        for (const rule of deliveryRules) {
            const deliveryRoute = new DeliveryRoute(rule);
            const key = deliveryRoute.getKey();
            if (!key) {
                continue;
            }
            if (this.deliveryRoutesByWeekDay[key] == null) {
                this.deliveryRoutesByWeekDay[key] = [];
            }
            this.deliveryRoutesByWeekDay[key].push(deliveryRoute);
            this.addToRegionMap(deliveryRoute, key);
        }
    }
    constructor(deliveryRules) {
        this.deliveryRoutesByWeekDay = {};
        this.deliveryRoutesByRegion = {};
        if (deliveryRules) {
            this.initializeRules(deliveryRules);
        }
    }
    addToRegionMap(deliveryRule, key) {
        const region = deliveryRule.getRegion();
        if (!this.deliveryRoutesByRegion[region]) {
            this.deliveryRoutesByRegion[region] = {};
        }
        if (!this.deliveryRoutesByRegion[region][key]) {
            this.deliveryRoutesByRegion[region][key] = [];
        }
        this.deliveryRoutesByRegion[region][key].push(deliveryRule);
    }
}
// ======================================
// DeliveryRulesCalculator
// ======================================
class DeliveryRulesCalculator extends DeliveryRules {
    applicableRules(rules, customerSelectedDeliveryRules) {
        if (rules == null || !Array.isArray(rules)) {
            return [];
        }
        const rulesSelectedByCustomer = rules.filter(rule => {
            if (!customerSelectedDeliveryRules) {
                return true;
            }
            if (!rule.delivery?.weekday) {
                return true;
            }
            return customerSelectedDeliveryRules[rule.delivery.weekday];
        });
        const zipCodeRelatedRules = rulesSelectedByCustomer.filter(rule => {
            if (!this.zipCode || !rule.applicableWhen?.validZipCodes) {
                return false;
            }
            return (matchBasedOnZipCodeRange(this.zipCode, rule) ||
                matchBasedOnZipCodePattern(this.zipCode, rule));
        });
        if (zipCodeRelatedRules.length > 0) {
            return zipCodeRelatedRules;
        }
        return rulesSelectedByCustomer.filter(rule => {
            return (rule.applicableWhen?.validZipCodes?.[0] ===
                exports.DeliveryRulesGeneralRegion || !rule.applicableWhen?.validZipCodes);
        });
    }
    getDeliveryRulesByPriority() {
        let selectedDeliveryRules = null;
        if (this.supplierConnection?.deliveryRules) {
            selectedDeliveryRules = {};
            this.supplierConnection.deliveryRules.map(rule => {
                if (rule.selectedRoute?.weekday) {
                    selectedDeliveryRules[rule.selectedRoute.weekday] = true;
                }
            });
        }
        else if (this.customerSubUnit?.deliveryRules) {
            selectedDeliveryRules = {};
            this.customerSubUnit.deliveryRules.map(rule => {
                if (rule.selectedRoute?.weekday) {
                    selectedDeliveryRules[rule.selectedRoute.weekday] = true;
                }
            });
        }
        if (this.supplierCustomer?.deliveryRules) {
            return this.applicableRules(this.supplierCustomer.deliveryRules, selectedDeliveryRules);
        }
        if (this.orderFullfillmentCenter?.deliveryRules) {
            return this.applicableRules(this.orderFullfillmentCenter.deliveryRules, selectedDeliveryRules);
        }
        return [];
    }
    constructor(orderFullfillmentCenter, customerSubUnit, supplierCustomer, supplierConnection) {
        super(null);
        this.orderFullfillmentCenter = orderFullfillmentCenter;
        this.supplierCustomer = supplierCustomer;
        this.customerSubUnit = customerSubUnit;
        this.supplierConnection = supplierConnection;
        if (this.supplierCustomer?.deliveryZipCode) {
            this.zipCode = this.supplierCustomer?.deliveryZipCode;
        }
        else {
            this.zipCode =
                this.customerSubUnit?.deliveryZipCode ||
                    this.customerSubUnit?.invoiceZipCode;
        }
        if (this.zipCode) {
            this.zipCode = this.zipCode.replace(' ', '');
            this.zipCode = this.zipCode.replace('-', '');
        }
        let deliveryRules = this.getDeliveryRulesByPriority();
        if (this.orderFullfillmentCenter?.deliveryRuleOverrides) {
            deliveryRules = [
                ...deliveryRules,
                ...this.applicableRules(this.orderFullfillmentCenter?.deliveryRuleOverrides, null),
            ];
        }
        this.initializeRules(deliveryRules);
    }
    isPossibleToGetDeliveryOnDate(date, now) {
        if (now == null) {
            now = new Date(Date.now());
        }
        const day = weekdaysMapping[(0, moment_timezone_1.default)(date).weekday()];
        if (this.deliveryRoutesByWeekDay[day]) {
            const dayDate = (0, moment_timezone_1.default)(date).format('YYYY-MM-DD');
            let rules = [];
            if (this.deliveryRoutesByWeekDay[dayDate]) {
                rules = this.deliveryRoutesByWeekDay[dayDate];
            }
            else {
                rules = this.deliveryRoutesByWeekDay[day];
            }
            if (rules.length > 0) {
                for (const deliveryRule of rules) {
                    const breakTimeForRule = deliveryRule.getBreakTimeForDate(date, this.zipCode);
                    if (!lodash_1.default.isNil(breakTimeForRule) && breakTimeForRule > now) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    getDeliverableDays(fromDateTime, untilDateTime) {
        const result = {};
        const from = (0, moment_timezone_1.default)(fromDateTime ?? new Date(Date.now())); // här kommer det bli knas med tidszoner date.now() kommer ge olika svar på server och klienten
        const to = (0, moment_timezone_1.default)(untilDateTime ?? from.clone().add(43, 'days'));
        for (let m = from.clone(); m.isBefore(to); m.add(1, 'days')) {
            if (this.isPossibleToGetDeliveryOnDate(m.toDate(), from.toDate())) {
                result[m.format('YYYY-MM-DD')] = true;
            }
        }
        return Object.keys(result);
    }
    getEarliestPossibleDeliveryDate(numberOfExtraDeliveryDays) {
        const earliestDeliveryDateArticle = (0, moment_timezone_1.default)(moment_timezone_1.default.now())
            .add(numberOfExtraDeliveryDays, 'days')
            .toDate();
        const possibleDeliveryDays = this.getDeliverableDays(earliestDeliveryDateArticle);
        return lodash_1.default.first(possibleDeliveryDays);
    }
    checkIfCanDeliverOnOrBeforeDate(requestedDeliveryDate, numberOfExtraDeliveryDays) {
        if (!numberOfExtraDeliveryDays || numberOfExtraDeliveryDays === 0)
            return true;
        if (!requestedDeliveryDate && numberOfExtraDeliveryDays > 0) {
            return false;
        }
        const firstPossibleDeliveryDate = this.getEarliestPossibleDeliveryDate(numberOfExtraDeliveryDays);
        const requestedDate = (0, moment_timezone_1.default)(requestedDeliveryDate);
        if ((0, moment_timezone_1.default)(firstPossibleDeliveryDate).isAfter(requestedDate, 'day')) {
            return false;
        }
        return true;
    }
}
exports.DeliveryRulesCalculator = DeliveryRulesCalculator;
// ======================================
// DeliveryRulesEditor
// ======================================
class DeliveryRulesEditor extends DeliveryRules {
    constructor(deliveryRules) {
        super(deliveryRules);
    }
    removeRule(region, key) {
        if (!this.deliveryRoutesByRegion[region]) {
            return;
        }
        delete this.deliveryRoutesByRegion[region][key];
        if (!this.deliveryRoutesByWeekDay[key]) {
            return;
        }
        this.deliveryRoutesByWeekDay[key] = this.deliveryRoutesByWeekDay[key].filter((deliveryRule) => {
            return deliveryRule.getRegion() !== region;
        });
    }
    getOrCreateRuleWithRegionAndWeekday(region, weekday) {
        const result = this.getRuleWithRegionAndWeekday(region, weekday);
        if (result) {
            return result;
        }
        this.addDeliveryRuleForWeekDay(weekday, '12:00', 1, false, region);
        return this.getRuleWithRegionAndWeekday(region, weekday);
    }
    getRuleWithRegionAndWeekday(region, weekday) {
        return this.deliveryRoutesByRegion[region]?.[weekday]?.[0];
    }
    addRule(deliveryRule) {
        const rawRules = this.getRawRules() || [];
        this.initializeRules([...rawRules, deliveryRule.getDefinition()]);
    }
    setRules(deliveryRules) {
        this.initializeRules(deliveryRules ?? []);
    }
    getRulesWithRegion(region) {
        return Object.values(this.deliveryRoutesByRegion[region] ?? []).flat();
    }
    getRawRules() {
        const ruleObjects = Object.values(this.deliveryRoutesByWeekDay).flat();
        const rules = ruleObjects.map((rule) => rule.getDefinition());
        if (!rules || rules.length === 0) {
            return null;
        }
        // Remove duplicate objects by converting them to json strings and using lodash uniq
        return lodash_1.default.uniqWith(rules, lodash_1.default.isEqual);
    }
    getUniqueRegions() {
        return Object.keys(this.deliveryRoutesByRegion);
    }
    changeRegion(oldRegion, newRegion) {
        if (oldRegion === newRegion) {
            return;
        }
        if (this.deliveryRoutesByRegion && this.deliveryRoutesByRegion[oldRegion]) {
            for (const key of Object.keys(this.deliveryRoutesByRegion[oldRegion])) {
                if (this.deliveryRoutesByRegion[oldRegion] &&
                    this.deliveryRoutesByRegion[oldRegion][key]) {
                    for (const rule of this.deliveryRoutesByRegion[oldRegion][key]) {
                        rule.setRegion(newRegion);
                    }
                }
            }
        }
        const rules = this.deliveryRoutesByRegion[oldRegion];
        delete this.deliveryRoutesByRegion[oldRegion];
        if (rules) {
            this.deliveryRoutesByRegion[newRegion] = rules;
        }
    }
    // Delivery rules manipulation
    addDeliveryRuleForWeekDay(deliveryWeekDay, orderByTime, orderDaysBefore, blocked, region) {
        const deliveryRule = DeliveryRoute.deliveryRouteFromProps({
            region,
            deliveryWeekDay,
            orderByTime,
            orderDaysBefore,
            blocked,
        });
        this.addDeliveryRuleObject(deliveryRule);
    }
    addDeliveryRuleForDate(date, orderByTime, orderDaysBefore, blocked, region) {
        const deliveryRule = DeliveryRoute.deliveryRouteFromProps({
            region,
            date,
            orderByTime,
            orderDaysBefore,
            blocked,
        });
        this.addDeliveryRuleObject(deliveryRule);
    }
    addDeliveryRuleObject(deliveryRule) {
        const deliveryRuleKey = deliveryRule.getKey();
        if (deliveryRuleKey != null) {
            if (this.deliveryRoutesByWeekDay[deliveryRuleKey] == null) {
                this.deliveryRoutesByWeekDay[deliveryRuleKey] = [];
            }
            if (this.deliveryRoutesByWeekDay[deliveryRuleKey]) {
                this.deliveryRoutesByWeekDay[deliveryRuleKey].push(deliveryRule);
            }
            this.addToRegionMap(deliveryRule, deliveryRuleKey);
        }
    }
}
exports.DeliveryRulesEditor = DeliveryRulesEditor;
class DeliveryRulesPrettyPrinter {
    constructor(deliveryRules) {
        this.deliveryRules = deliveryRules || customerSubUnit_1.defaultSelectedDeliveryRules;
    }
    getDeliveryRulesAsOpeningHourString() {
        if (!this.deliveryRules) {
            return '';
        }
        return this.deliveryRules
            .filter(rule => !!rule.selectedRoute?.weekday)
            .map(rule => {
            return `${(0, exports.getWeekdayName)(rule.selectedRoute?.weekday)}: Öppet`;
        })
            .join(', ');
    }
}
exports.DeliveryRulesPrettyPrinter = DeliveryRulesPrettyPrinter;
